import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Paper,
  Typography,
  Modal,
  Grid,
  IconButton,
  Divider,
  FormHelperText,
  Autocomplete,
  Chip,
  SelectChangeEvent,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { StockItem } from '../../models/stocks';
import { getProductsByBranch, transferStocks } from '../../api/stock-transfer/stock-transfer';

interface Location {
  id: number;
  name: string;
  branch_id: string;
}

interface TransferFormProps {
  items: StockItem[];
  locations: Location[]; 
  vendors: any[];
  userRole: 'bar' | 'warehouse' | 'admin';
  onSubmit: (transfer: any) => void;
  handleClose: () => void;
  openModal: boolean;
}

const TransferForm: React.FC<TransferFormProps> = ({
  items = [],
  locations: locationsProp = [],
  vendors = [],
  userRole,
  onSubmit,
  openModal = false,
  handleClose,
}) => {
  const locations = Array.isArray(locationsProp)
    ? locationsProp
    : locationsProp?.data || [];
  
  const [formData, setFormData] = useState({
    itemId: '',
    itemName: '',
    quantity: 0,
    quantity2: 0,
    fromLocation: '',
    toLocation: '',
    notes: '',
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [errors, setErrors] = useState({
    itemId: false,
    quantity: false,
    fromLocation: false,
    toLocation: false,
  });

  const [branchProducts, setBranchProducts] = useState<StockItem[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  // Set default fromLocation based on user role
  useEffect(() => {
    if (!Array.isArray(locations) || locations.length === 0) return;

    let defaultLocation;
    if (userRole === 'bar') {
      defaultLocation = locations.find(loc => loc && loc.name && loc.name.toLowerCase().includes('bar'));
    } else if (userRole === 'warehouse') {
      defaultLocation = locations.find(loc => loc && loc.name && loc.name.toLowerCase().includes('warehouse'));
    }

    if (defaultLocation) {
      setFormData(prev => ({ ...prev, fromLocation: defaultLocation.branch_id }));
      // Load products for default branch
      fetchProductsByBranch(defaultLocation.branch_id);
    }
  }, [userRole, locations]);

  // Fetch products by branch
  const fetchProductsByBranch = async (branchId: string) => {
    if (!branchId) return;
    
    setLoadingProducts(true);
    try {
      const response = await getProductsByBranch(branchId);
      console.log('Products fetched for branch:', response);
    //  console.log('Response data:', response.data);
     // console.log('Response data data:', response.data.data);
      setBranchProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products by branch:', error);
      setBranchProducts([]);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleFromLocationChange = async (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      fromLocation: value,
      itemId: '',
      itemName: '',
      quantity: 0,
      quantity2: 0
    }));
    setSearchTerm('');
    setBranchProducts([]);
    
    if (errors.fromLocation) {
      setErrors(prev => ({ ...prev, fromLocation: false }));
    }

    // Fetch products for the selected branch
    await fetchProductsByBranch(value);
  };

  const handleToLocationChange = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    setFormData(prev => ({ ...prev, toLocation: value }));
    if (errors.toLocation) {
      setErrors(prev => ({ ...prev, toLocation: false }));
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    if (name === "quantity") {
      let val = Number(value);
      if (val < 1) val = 0;
      if (formData.quantity2 && val > formData.quantity2) {
        val = formData.quantity2;
      }
      setFormData(prev => ({ ...prev, [name]: val }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [name]: false }));
    }
  };

  // const optionsToShow = useMemo(() => {
  //  // if (!searchTerm || searchTerm.length < 3) return [];
  //     if (!searchTerm) return []; // show nothing if searchTerm empty

  //   return branchProducts.filter(item =>
  //     item.name &&
  //     item.name.toLowerCase().includes(searchTerm.toLowerCase())
  //   );
  // }, [searchTerm, branchProducts]);
const optionsToShow = useMemo(() => {
  // console.log('Branch Products:', branchProducts);
  // console.log('Search Term:', searchTerm);
  if (!searchTerm || searchTerm.length < 3) return [];
  return branchProducts.filter(item =>
    item.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );
}, [searchTerm, branchProducts]);
  const validateForm = () => {
    const newErrors = {
      itemId: !formData.itemId,
      quantity: !formData.quantity || Number(formData.quantity) <= 0,
      fromLocation: !formData.fromLocation,
      toLocation: !formData.toLocation || formData.toLocation === formData.fromLocation,
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some(Boolean);
  };

  const [successMessage, setSuccessMessage] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const handleSubmit = async(e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        const response = await transferStocks(formData);
        if (response.data.status === true) {
          setSuccessMessage(`Stock transfer successful!`);
          alert(`Stock transfer successful!\nTransferred quantity: ${formData.quantity}`);
          setOpenSnackbar(true);
        } else {
          setSuccessMessage('Stock transfer failed. Please try again.');
          setOpenSnackbar(true);
        }
        handleClose();
      } catch (error) {
        console.error(error);
        setSuccessMessage('An error occurred. Please try again.');
        setOpenSnackbar(true);
      }
    }
  };

  const selectedItem = branchProducts.find(item => item && item.product_id === formData.itemId);

  const getAvailableDestinations = () => {
    if (!Array.isArray(locations)) return [];
    return locations.filter(loc => loc && loc.branch_id !== formData.fromLocation);
  };

  return (
    <Modal 
      open={openModal} 
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper sx={{ width: '90%', maxWidth: '600px', p: 3, position: 'relative' }}>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
          Stock Transfer 
        </Typography>
        <Divider sx={{ my: 2 }} />

        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            {/* Step 1: From Location */}
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth error={errors.fromLocation}>
                <InputLabel id="from-location-label">From Location *</InputLabel>
                <Select
                  labelId="from-location-label"
                  name="fromLocation"
                  value={formData.fromLocation}
                  onChange={handleFromLocationChange}
                  label="From Location *"
                >
                  {locations.map((location) => {
                    if (!location || !location.branch_id) return null;
                    return (
                      <MenuItem key={location.branch_id} value={location.branch_id}>
                        {location.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.fromLocation && <FormHelperText>Please select source location</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Step 2: Product Search (only enabled when fromLocation is selected) */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <Autocomplete
                
                  options={optionsToShow}
                  getOptionLabel={(option) => `${option.name} - Stock: ${option.qty}`}
                  isOptionEqualToValue={(option, value) => option.product_id === value.product_id}
                  inputValue={searchTerm}
                  onInputChange={(_, newInputValue) => setSearchTerm(newInputValue)}
                  onChange={(_, newValue) => {
                    if (newValue) {
                      setFormData(prev => ({
                        ...prev,
                        itemId: newValue.product_id,
                        itemName: newValue.name,
                        quantity: Number(newValue.qty),
                        quantity2: Number(newValue.qty),
                      }));
                      setErrors(prev => ({ ...prev, itemId: false }));
                    }
                  }}
                  disabled={!formData.fromLocation || loadingProducts}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search Product *"
                      error={errors.itemId}
                      helperText={errors.itemId ? "Please select a product" : "Type at least 3 characters to search"}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <SearchIcon sx={{ color: 'action.active', mr: 1 }} />
                            {loadingProducts && <CircularProgress size={20} sx={{ mr: 1 }} />}
                            {params.InputProps.startAdornment}
                          </>
                        ),
                      }}
                      placeholder={formData.fromLocation ? "Type at least 3 characters to search" : "Select From Location first"}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} key={option.product_id}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography>{option.name}</Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
<Chip
  label={`Selected Branch Balance Stock: ${
    option.qty && Number(option.qty) !== 0
      ? Number(option.qty).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '0.00'
  }`}
  size="small"
  variant="outlined"
/>                      
                        </Box>
                      </Box>
                    </li>
                  )}
                  noOptionsText={searchTerm.length < 3 ? "Type at least 3 characters to search" : "No products found"}
                />
              </FormControl>
            </Grid>

            {/* Step 3: To Location */}
            <Grid item xs={12} sm={12}>
              <FormControl fullWidth error={errors.toLocation}>
                <InputLabel id="to-location-label">To Location *</InputLabel>
                <Select
                  labelId="to-location-label"
                  name="toLocation"
                  value={formData.toLocation}
                  onChange={handleToLocationChange}
                  label="To Location *"
                  disabled={!formData.itemId}
                >
                  {getAvailableDestinations().map((location) => {
                    if (!location || !location.branch_id) return null;
                    return (
                      <MenuItem key={location.branch_id} value={location.branch_id}>
                        {location.name}
                      </MenuItem>
                    );
                  })}
                </Select>
                {errors.toLocation && <FormHelperText>Please select destination</FormHelperText>}
              </FormControl>
            </Grid>

            {/* Step 4: Quantity */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Quantity *"
                name="quantity"
                type="number"
                value={formData.quantity}
                onChange={handleChange}
                error={errors.quantity}
                helperText={errors.quantity ? "Please enter valid quantity" : ""}
                inputProps={{ min: 1, max: formData.quantity2 }}
                disabled={!formData.itemId}
              />
              {formData.quantity2 > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Max available: {formData.quantity2}
                </Typography>
              )}
            </Grid>

            {/* Step 5: Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                multiline
                rows={3}
                value={formData.notes}
                onChange={handleChange}
                placeholder="Optional notes about this transfer"
                disabled={!formData.itemId}
              />
            </Grid>
          </Grid>

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={handleClose} sx={{ px: 4 }}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              sx={{ px: 4 }}
              disabled={!formData.itemId || !formData.quantity || !formData.fromLocation || !formData.toLocation}
            >
              Confirm Transfer
            </Button>
          </Box>
        </Box>
      </Paper>
    </Modal>
  );
};

export default TransferForm;